import { getIANATimezoneStringForAbbreviation, printNameOrEmail } from "./sf_utils";
import { format } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { phoneNumberFormmaterString } from "pages/Admin/Users/UserComponents/UserList/Formatters";
import jsPDF from 'jspdf';
import { NO_QA_MESSAGE, NO_SUMMARY_MESSAGE, REPORTING_TIMEZONE, logoDark } from "./sf_constants";
import PoppinsRegular from '../../src/assets/fonts/Poppins-Regular.ttf' //Source: https://fonts.google.com/specimen/Poppins
import PoppinsBold from '../../src/assets/fonts/Poppins-Bold.ttf'
import { isGoalBrand } from "config";
const BULLET_POINT_SYMBOL = "\u2022";
const SUMMARY_SPLIT_REGEX = /\n-|\n -/
const CONVERSATIONS_FILTERS = {
    DATE_RANGE: 'date_range',
}

const HEADER_FIELDS = {
    NAME: 'Name',
    CALL_RECEIVED: 'Call Received',
    PHONE: 'Phone'
}

const FONT_STYLES = {
    BOLD: 'bold',
    REGULAR: 'regular',
    NORMAL: 'normal'
}

const POPPINS = 'Poppins'

/**
 * @brief We wanted to implement the call page filters using the new approach of data filtering. There was a difference between how we were sending the data as payload in previous call filters and new approach.
 *
 * Consider an example: For First Name filter in new approach the payload to the API looks something like this filters: [{"name":"first_name","comparator_value":"","component_type":"text","value":"John"}]
 * 
 * But the calls filtering backend logic doesn't support this payload because the old approach payload looks something like this filters: {"first_name":true,"first_name_value":"John"}
 * 
 * So the mapActiveFilters is responsible for changing the new approach payload to the old one
 * @param {array} prospectFilters the payload in the format of new approach
 * @param {array} selectedTagItems special cases where we need to pass selected_value as one more attribute
 * @return the payload in the old approach format
 */

export const mapActiveFilters = (prospectFilters, selectedTagItems) => {
    return prospectFilters.reduce((result, item) => {
        if (!item.is_active) return result;

        let filter_name = item?.name;
        let isSelecteTagItems = selectedTagItems.includes(filter_name);

        let filterValue;
        if (item.options) {
            filterValue = JSON.stringify(item.value);
        } else if (item.component_type === CONVERSATIONS_FILTERS.DATE_RANGE) {
            filterValue = JSON.stringify(item.value?.value);
        } else {
            filterValue = item.value;
        }

        result[filter_name] = true;
        result[`${filter_name}_value`] = filterValue;

        // For selected_tag items we need to add selected_value as one more attribute 
        // For example: For call duration filter we need the payload to be
        // filters: {"call_duration":true,"call_duration_value":"12","selected_call_duration_value":"Equal To"}


        if (isSelecteTagItems) {
            result[`selected_${filter_name}_value`] = item?.value?.label;
            result[`${filter_name}_value`] = item.comparator_value;
        }

        if (item.component_type === CONVERSATIONS_FILTERS.DATE_RANGE) {
            result[`${filter_name}_value_label`] = item?.value?.original_label;
        }

        return result;
    }, {});
};

/**
 * @brief Function filters out active filters and creates an array of objects that has the following structure
 *
 *  [{"name":"first_name","comparator_value":"","component_type":"text","value":"John"}]
 * 
 * @param {array} prospectFilters payload as an array of objects
 * @return filtered out active filters
 */

export const mapActiveFiltersForProspect = (prospectFilters) => prospectFilters.filter(item => item.is_active).map(item => ({ name: item.name, comparator_value: item.comparator_value, component_type: item.component_type, value: item.value }));

export const handlePdfExport = (callSummary, callDetails, overallConversation) => {
    const doc = new jsPDF();
    doc.addFont(PoppinsRegular, POPPINS, FONT_STYLES.NORMAL);
    doc.addFont(PoppinsBold, POPPINS, FONT_STYLES.BOLD);
    doc.setFont(POPPINS, FONT_STYLES.NORMAL);
    doc.setFontSize(11);
    const summary = isGoalBrand ? callSummary?.data?.qf_quote_call_summary?.summary : callSummary?.data?.quote_call_summary?.summary || '';
    const conversations = overallConversation || [];
    let yOffset = 60;
    const pageWidth = doc.internal.pageSize.width;
    const padding = 10;
    const textWidth = pageWidth - padding;
    
    doc.addImage(logoDark, 'JPEG', 9, 10, isGoalBrand ? 35 : 60, 10);

    const fields = [
        { label: HEADER_FIELDS.NAME, value: printNameOrEmail(callDetails?.lead_detail?.first_name, callDetails?.lead_detail?.last_name, null, '-- --') },
        { label: HEADER_FIELDS.CALL_RECEIVED, value: callDetails?.created_at ? format(utcToZonedTime(callDetails?.created_at, getIANATimezoneStringForAbbreviation(REPORTING_TIMEZONE)), 'MM/dd/yyyy hh:mm aa') : '' },
        { label: HEADER_FIELDS.PHONE, value: !isNaN(callDetails?.lead_detail?.phone) && callDetails?.lead_detail?.phone ? phoneNumberFormmaterString(callDetails?.lead_detail?.phone) : '-- --' }
    ];

    let yPos = 30;
    const verticalSpacing = 7;
    fields.forEach((field) => {
        const xpos = padding;
        if (field.label === HEADER_FIELDS.NAME) {
            doc.setFont(POPPINS, FONT_STYLES.BOLD);
        }
        doc.text(`${field.label}: ${field.value}`, xpos, yPos);
        yPos += verticalSpacing;
        doc.setFont(POPPINS, FONT_STYLES.NORMAL);
    });

    if (summary) {
        doc.setFont(POPPINS, FONT_STYLES.BOLD);
        doc.text('Call Summary', padding, yOffset);
        yOffset += 10;
    
        const BULLET_INDENT = padding;
        const TEXT_INDENT = BULLET_INDENT + 4;
        const LINE_HEIGHT = 6;
    
        doc.setFont(POPPINS, FONT_STYLES.NORMAL);
    
        const sentences = summary.split(SUMMARY_SPLIT_REGEX).map(s => s.trim()).filter(Boolean);
    
        sentences.forEach((sentence, index) => {
            let text = sentence;
            if (index > 0) {
                text = text.replace(/^-\s*/, '').trim();
            }
    
            const lines = doc.splitTextToSize(text, textWidth - TEXT_INDENT);
    
            if (yOffset + lines.length * LINE_HEIGHT + 10 > doc.internal.pageSize.height - 20) {
                doc.addPage();
                yOffset = 20;
            }

            // Always add bullet points
            if (lines.length > 0) {
                doc.text(BULLET_POINT_SYMBOL, BULLET_INDENT, yOffset);
                doc.text(lines[0], TEXT_INDENT, yOffset);
                yOffset += LINE_HEIGHT;

                for (let i = 1; i < lines.length; i++) {
                    doc.text(lines[i], TEXT_INDENT, yOffset);
                    yOffset += LINE_HEIGHT;
                }
            }
            yOffset += 3;
        });
    } else {
        doc.text(NO_SUMMARY_MESSAGE, padding, yOffset);
        yOffset += 20;
    }
    if (conversations.length > 0) {
        doc.setFont(POPPINS, FONT_STYLES.BOLD);
        doc.text('Call Q&A', padding, yOffset + 20);
        yOffset += 30;
    
        const QUESTION_INDENT = padding;
        const BULLET_INDENT = padding;
        const HANGING_INDENT = BULLET_INDENT + 4;
        const LINE_HEIGHT = 6;
    
        conversations.forEach(({ question, answer }, convoIndex) => {
            // --- Question ---
            doc.setFont(POPPINS, FONT_STYLES.BOLD);
            const splitQuestion = doc.splitTextToSize(question, textWidth - QUESTION_INDENT);
    
            if (yOffset + splitQuestion.length * LINE_HEIGHT + 20 > doc.internal.pageSize.height - 20) {
                doc.addPage();
                yOffset = 20;
            }
    
            doc.text(splitQuestion, QUESTION_INDENT, yOffset);
            yOffset += splitQuestion.length * LINE_HEIGHT + 6; // consistent gap after question
    
            // --- Answers ---
            if (answer && answer.trim()) {
                doc.setFont(POPPINS, FONT_STYLES.NORMAL);
                const lines = answer.split('\n').map(l => l.trim()).filter(Boolean);
    
                lines.forEach((line, lineIndex) => {
                    // Clean up any leading dashes
                    let text = line.replace(/^-?\s*/, '');
                    if (!text) return;
    
                    const fullText = `${BULLET_POINT_SYMBOL}  ${text}`;
                    const splitLines = doc.splitTextToSize(fullText, textWidth - HANGING_INDENT);
    
                    // Page break check
                    if (yOffset + splitLines.length * LINE_HEIGHT + 8 > doc.internal.pageSize.height - 20) {
                        doc.addPage();
                        yOffset = 20;
                    }
    
                    // First line: bullet + text
                    if (splitLines.length > 0) {
                        const firstLineText = splitLines[0].replace(`${BULLET_POINT_SYMBOL}  `, '');
                        doc.text(BULLET_POINT_SYMBOL, BULLET_INDENT, yOffset);
                        doc.text(firstLineText, HANGING_INDENT, yOffset);
                        yOffset += LINE_HEIGHT;
                    }
    
                    // Wrapped lines
                    for (let i = 1; i < splitLines.length; i++) {
                        doc.text(splitLines[i], HANGING_INDENT, yOffset);
                        yOffset += LINE_HEIGHT;
                    }
    
                    // Add small gap AFTER each bullet item (except if it's the last in entire answer)
                    if (lineIndex < lines.length - 1) {
                        yOffset += 2; // tight spacing between bullets in same answer
                    }
                });
    
                // Only add bigger gap after the entire answer block
                yOffset += 6;
            } else {
                doc.setFont(POPPINS, FONT_STYLES.ITALIC);
                doc.text('No response', padding + 8, yOffset);
                yOffset += LINE_HEIGHT + 6;
            }
    
            // Add consistent spacing before next Q&A (except last one if needed)
            if (convoIndex < conversations.length - 1) {
                yOffset += 8;
            }
        });
    } else {
        doc.text(NO_QA_MESSAGE, padding, yOffset + 20);
    }
    doc.save("call_summary");
};